import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import {
  AddIcon,
  Button,
  FlexContent,
  InlineOrderEditor,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
  useDebounce,
} from "@rothoblaas/design-system";
import Table from "../../../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import Visibility from "../../../../components/ui/Visibility";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { type TimberClass } from "../../../../http/beamReinforcement/types/timberClass";
import { useBRTimbersApi } from "../../../../http/beamReinforcement/useAdminTimbers";
import { PaginationQuery } from "../../../../types";
import TimberClassFormDialog from "./TimberClassFormDialog";

const TimberClassesTab: FC = () => {
  const i18n = useI18n();
  const filters = useListFilters({ isSearchable: true });
  const aborter = useRef(new AbortController());
  const timbersApi = useBRTimbersApi();
  const dispatch = useContext(NotificationDispatch);
  const [itemToDelete, setItemToDelete] = useState<TimberClass>();
  const [itemToEdit, setItemToEdit] = useState<TimberClass>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(filters.state.q);
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = timbersApi.useBRTimbers(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);
  const t = i18n.translation.beamReinforcementModule.timbers;

  const updateVisibility = async (item: TimberClass, visibility: "public" | "internal" | "hidden") => {
    try {
      await timbersApi.updateBRTimberVisibility(item.name, { visibility });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (name: string, newOrder: number) => {
    try {
      await timbersApi.updateBRTimberOrder(name, { order: newOrder });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  if (!data && !error && !isInitialized) {
    return null;
  }

  return (
    <FlexContent padding>
      <Table<TimberClass>
        filters={filters}
        search={{
          placeholder: `${i18n.translation.common.search}...`,
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        headingActions={
          <Button onClick={() => setIsCreateOpen(true)}><Icon glyph={AddIcon} />
            {i18n.translation.common.add}
          </Button>
        }
        head={
          <TableRow>
            <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
            <TableHeadCell align="left">{t.fields.density}</TableHeadCell>
            <TableHeadCell align="left">{t.fields.bendingStrength}</TableHeadCell>
            <TableHeadCell align="left">{t.fields.shearStrength}</TableHeadCell>
            <TableHeadCell align="left">{t.fields.modulusOfElasticity}</TableHeadCell>
            <TableHeadCell align="left">{t.fields.isGlulam}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.common.visibility}</TableHeadCell>
            <TableHeadCell align="center">{i18n.translation.common.order}</TableHeadCell>
            <TableHeadCell align="left"></TableHeadCell>
          </TableRow>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <TableCell>{item.name}</TableCell>
            <TableCell>{item.density}</TableCell>
            <TableCell>{item.bendingStrength}</TableCell>
            <TableCell>{item.shearStrength}</TableCell>
            <TableCell>{item.modulusOfElasticity}</TableCell>
            <TableCell>
              {item.isGlulam
                ? i18n.translation.beamReinforcementModule.details.yes
                : i18n.translation.beamReinforcementModule.details.no}
            </TableCell>
            <TableCell>
              <Visibility
                value={item.visibility}
                onChange={(nextVisibility) => updateVisibility(item, nextVisibility)}
              />
            </TableCell>
            <TableCell align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.name, newOrder)} />
            </TableCell>
          </>
        )}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        onRowClick={(item) => setItemToEdit(item)}
        total={data?.total}
        error={!!error}
      />

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={t.singular}
          isSubmitting={isSubmitting}
          buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                setIsSubmitting(true);
                timbersApi
                  .deleteBRTimber(itemToDelete.name)
                  .then(() => {
                    dispatch(showSuccessNotification(i18n.translation));
                    setItemToDelete(undefined);
                  })
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              }}>{i18n.translation.common.yes}</Button>]}
        >
          {i18n.translation.clients.questions.delete}
        </ModalDialog>
      )}

      {isCreateOpen && <TimberClassFormDialog onClose={() => setIsCreateOpen(false)} />}
      {!!itemToEdit && <TimberClassFormDialog timber={itemToEdit} onClose={() => setItemToEdit(undefined)} />}
    </FlexContent>
  );
};

export default TimberClassesTab;
